import { Button, Divider, Flex, Group, Text } from "@mantine/core";
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useTheme } from "src/hooks";
import { type ICheckList, type ICheckListItem } from "src/interfaces";
import { useSelector } from "src/redux/hooks";
import { pushNewEntryActionsData } from "src/redux/slices/entrySlice";
import { setCheckList } from "src/redux/slices/generatorSlice";

import ChecklistDescription from "../../../../components/shared/ChecklistDescription";
import { getHistoryRecordName } from "../checklists.utils";
import { checkBaseField } from "../../../utils/getCheckListProgress";
import CheckListsFormItem from "./CheckListsFormItem";
import { DateTimeDisp } from "src/components/shared/DateTime";
import { useTriggerAutoSave } from "src/generator/hooks/useTriggerAutoSave";
import { useOfflineChecklistFlush } from "src/generator/hooks/useOfflineChecklistFlush";
interface Props extends ICheckList {
  yachtid: string | any;
  reset: () => void;
  disabled?: boolean;
  hideEntryDate?: boolean;
  preview?: boolean;
  close: () => void;
  selectedItemIndex: number | null;
  renderedDesc: string;
}

const CheckListsForm = forwardRef(
  (
    {
      reset,
      disabled,
      hideEntryDate,
      preview,
      close,
      selectedItemIndex,
      renderedDesc,
      ...props
    }: Props,
    ref,
  ) => {
    const dispatch = useDispatch();
    const { colors } = useTheme();

    const triggerAutoSave = useTriggerAutoSave();
    const flushOfflineChecklist = useOfflineChecklistFlush();
    const setValue = useFormContext()?.setValue;
    const id = useSelector(
      state => state.generatorSlice.checkListsModalProps.id,
    );
    const latestEntryLbName = useSelector(
      state => state.generatorSlice.latestEntry.lbName,
    );
    const logbooks = useSelector(state => state.generatorSlice.logbooks);
    const lbName =
      latestEntryLbName ||
      logbooks.find(logbook => logbook.value === props.currentLogbook)
        ?.label ||
      "";
    const [selectedCheckList, setSelecedCheckList] = useState(props);
    const checks = useMemo(
      () =>
        selectedItemIndex !== null
          ? (
              selectedCheckList.data.list[selectedItemIndex] as unknown as {
                items: ICheckListItem[];
              }
            ).items
          : selectedCheckList.data.list,
      [selectedItemIndex, selectedCheckList],
    );

    const allChecklistItems = useMemo(
      () =>
        selectedCheckList.data.type === "group"
          ? (
              selectedCheckList.data.list as unknown as {
                items: ICheckListItem[];
              }[]
            ).flatMap(group => group.items ?? [])
          : (selectedCheckList.data.list as unknown as ICheckListItem[]),
      [selectedCheckList],
    );

    // Once the entry has an id, actions can be attributed to it immediately.
    const queueRid = props.currentEntryId ?? "";

    const getChecklistLabel = useCallback(() => {
      if (selectedItemIndex !== null) {
        return (
          selectedCheckList.name +
          " - " +
          selectedCheckList.data.list[selectedItemIndex].name
        );
      }
      return selectedCheckList.name;
    }, [selectedCheckList, selectedItemIndex]);

    const postUpdateChecklist = useCallback(
      (action: string, itemName: string, cb: any) => {
        dispatch(
          pushNewEntryActionsData({
            cid: props.cid,
            rid: queueRid,
            lbid: props.currentLogbook ?? "",
            lbname: lbName,
            yachtid: props.yachtid,
            action,
            item: itemName,
            url: "record-action-checklist",
          }),
        );
        if (cb) cb();
      },
      [props.cid, props.currentLogbook, props.yachtid, dispatch, queueRid, lbName],
    );

    const postCompleteChecklist = useCallback(
      (itemName: string) => {
        dispatch(
          pushNewEntryActionsData({
            url: "complete-checklist",
            cid: props.cid,
            rid: queueRid,
            lbid: props.currentLogbook ?? "",
            lbname: lbName,
            yachtid: props.yachtid,
            item: itemName,
          }),
        );
      },
      [props.cid, props.currentLogbook, props.yachtid, dispatch, queueRid, lbName],
    );

    const handleSubmit = useCallback(() => {
      if (disabled || preview) return;

      const hasChanges = checks.some(item => item.changed || item.naChanged);

      if (selectedCheckList?.data) {
        let reqSent: number = 0;
        let reqComplete: number = 0;

        const isChecklistComplete = allChecklistItems.every(
          item => item.unit === "header" || checkBaseField(item) === 1,
        );

        const finalCheck = (item: string) =>
          reqSent === reqComplete &&
          isChecklistComplete &&
          postCompleteChecklist(item);

        checks.forEach(
          v =>
            (v.changed || v.naChanged) &&
            (v.unit === "check" || v.unit === "text") &&
            reqSent++,
        );

        checks.forEach(item => {
          if (item.changed || item.naChanged) {
            const name = getChecklistLabel();

            postUpdateChecklist(
              getHistoryRecordName(item),
              `${name} :\n ${item.text}`,
              () => {
                reqComplete++;
                void finalCheck(name);
              },
            );
          }
        });

        if (reqSent === 0 && isChecklistComplete) {
          postCompleteChecklist(getChecklistLabel());
        }
      }

      if (hasChanges) {
        dispatch(setCheckList(selectedCheckList));
        // The list modal is still "opened" underneath while an item is being
        // saved, which would otherwise block this autosave.
        triggerAutoSave({ ignoreChecklistModalBlock: true });
      }

      flushOfflineChecklist(queueRid);

      if (id) {
        setValue(id, {
          name: selectedCheckList.name,
          cid: selectedCheckList.cid,
          selectedIndex: selectedItemIndex,
        });
      }
    }, [
      disabled,
      selectedCheckList,
      dispatch,
      id,
      checks,
      allChecklistItems,
      postCompleteChecklist,
      getChecklistLabel,
      selectedItemIndex,
      postUpdateChecklist,
      setValue,
      preview,
      triggerAutoSave,
      flushOfflineChecklist,
      queueRid,
    ]);

    useImperativeHandle(ref, () => ({
      save() {
        handleSubmit();
      },
    }));

    const isDagon = props.yachtid === "6621c93a-fff9-11ef-9167-02c1fe2db5a5";

    return (
      <>
        {!hideEntryDate && (
          <DateTimeDisp
            color={colors.grayText[0]}
            dir="row"
            size="sm"
            utc={selectedCheckList?.createddate}
            lt={selectedCheckList?.createddateLT}
            containerProps={{ mb: 10 }}
          />
        )}
        <Text fw={500} fz="sm" c="grayText.0" fs="italic">
          Description
        </Text>
        <ChecklistDescription desc={renderedDesc} styles={{ opacity: 0.7 }} />
        <Divider />

        <Flex direction="column" gap="sm" mt="sm">
          {checks.map((item, index) => (
            <CheckListsFormItem
              key={`checklist-item-${index}`}
              item={item}
              itemIndex={index}
              disabled={disabled || preview}
              selectedItemIndex={selectedItemIndex}
              setSelecedCheckList={setSelecedCheckList}
              reverseNaOrder={isDagon}
            />
          ))}
        </Flex>
        {!disabled && (
          <Group justify="flex-end" mt="sm">
            <Button
              onClick={() => {
                close();
              }}>
              {preview ? "Close" : "Save"}
            </Button>
          </Group>
        )}
      </>
    );
  },
);

export default CheckListsForm;
